import { SERVER_BASE_URL, env } from "@swivu/env";
import * as Sentry from "@sentry/browser";
import type { z } from "zod";

export const HTTPMethod = ["GET", "POST", "PUT"] as const;

declare global {
  interface ImportMetaEnv {
    readonly env: {
      ENV?: string;
    };
  }
}
export const HTTPStatusCode = [200] as const;

function urlWithParams<T extends Record<string, string>>(
  url: string,
  body: T
): string {
  return url.replace(/:\w+/g, (match) => {
    const key = match.slice(1);
    return body[key];
  });
}

export const uploadAPI = async <Request, Response>({
  method,
  url,
  requestSchema,
  responseSchema,
  body: bodyRaw,
  options = {},
  fileKey
}: {
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  url: string;
  requestSchema: z.ZodType<Request>;
  responseSchema: z.ZodType<Response>;
  body: Request;
  options?: RequestInit;
  fileKey: string;
}) => {
  try {
    requestSchema.parse(bodyRaw);

    const formData = new FormData();

    // Append all fields to FormData
    for (const [key, value] of Object.entries(
      bodyRaw as Record<string, unknown>
    )) {
      if (key === fileKey) {
        // Append file with the specified fileKey
        formData.append(key, value as File);
      } else if (value !== undefined && value !== null) {
        // Append other fields as strings
        formData.append(
          key,
          typeof value === "string" ? value : JSON.stringify(value)
        );
      }
    }

    const optionsWithDefault: RequestInit = {
      method,
      headers: {},
      body: formData,
      ...options,
      credentials: "include"
    };

    // regex the url to replace :param with the value from the body

    const parsedUrl = urlWithParams(url, bodyRaw ?? {});
    const baseUrl = window.SERVER_BASE_URL ?? SERVER_BASE_URL;
    const responseRaw = await fetch(baseUrl + parsedUrl, optionsWithDefault);
    // catch a fetch error and throw the entire error
    const responseText = await responseRaw.text();
    if (!responseRaw.ok) {
      // todo: log error to service
      throw new Error(
        `${responseRaw.status}: ${responseRaw.statusText}, ${responseText}`
      );
    }

    const response = JSON.parse(responseText);

    if (env.VITE_APP_NODE_ENV !== "development") {
      void responseSchema.safeParseAsync(response).then((result) => {
        if (!result.success) {
          try {
            Sentry.captureException(result.error, {
              extra: {
                url: parsedUrl,
                method,
                responseSample: (() => {
                  try {
                    const str = JSON.stringify(response);
                    return str.length > 1000 ? str.slice(0, 1000) + "…" : str;
                  } catch {
                    return "[unserializable response]";
                  }
                })()
              }
            });
          } catch {
            // ignore reporting failures
          }
        }
      });

      return response as Response;
    }

    return responseSchema.parse(response);
  } catch (error) {
    // todo log error to service
    if (error instanceof Error) {
      throw new Error(error.message);
    }
    throw new Error("Unknown error");
  }
};
